"use client";

import type { FC, Ref } from "react";

import { Card } from "../Card";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { Button, type ButtonProps } from "../Button";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { mergeRefs } from "../../utils";

import { containerStyle, contentStyle } from "./OcktoCard.css";

export type OcktoCardProps = {
  /**
   * ID
   */
  id: string;
  /**
   * Card title
   */
  title: string;
  /**
   * Card description
   */
  description: string;
  /**
   * Button label text
   */
  buttonValue: string;
  /**
   * Label for ContentMenu registration
   */
  label: string;
  /**
   * Callback when the button is clicked
   */
  onClick?: ButtonProps["onClick"];
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * Whether the card is visually active (shows animated gradient border).
   * Purely visual — does not set data-active or affect carousel scroll.
   */
  active?: boolean;
};

/**
 * A card for displaying an Ockto data import call-to-action
 * with a title, description, and action button.
 * Registers as a leading ContentMenu item with a QR icon.
 */
export const OcktoCard: FC<OcktoCardProps & { ref?: Ref<HTMLDivElement> }> = ({
  id,
  title,
  description,
  buttonValue,
  label,
  onClick,
  disabled,
  active,
  ref,
}) => {
  const sectionRef = useContentMenuSection<HTMLDivElement>({
    id,
    label,
    forcePosition: "leadingInside",
    icon: "qr",
  });

  return (
    <Card
      ref={mergeRefs(ref, sectionRef)}
      variant="infoSurface"
      padding="none"
      borderRadius="s"
      className={containerStyle}
      border={active ? "gradientAnimated" : "transparent"}
      onClick={disabled ? undefined : onClick}
      style={{ cursor: disabled ? undefined : "pointer" }}
    >
      <Flex className={contentStyle} direction="column" gap="s">
        <Text variant="displayS" bold>
          {title}
        </Text>
        <Text variant="bodyS" color="currentColor50">
          {description}
        </Text>
        <Flex grow align="flex-end">
          <Button disabled={disabled}>{buttonValue}</Button>
        </Flex>
      </Flex>
    </Card>
  );
};
